"use client"

import { useState, useRef, useEffect } from "react"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { motion } from "framer-motion"
import { Loader2, ArrowRight, Maximize2, Minimize2, FileText, Monitor, MousePointer2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { ShineBorder } from "@/components/ui/shine-border"
import { toast } from "sonner"
import { detectPitchIntent } from "@/lib/detect-pitch-intent"
import { useGetPlanStatusQuery } from "@/app/store/api/pricingApi"
import { useGetCartCreditStatusQuery } from "@/app/store/api/cartApi"
import { useAppSelector } from "@/app/store/hooks"

import { PitchDeckHeader } from "../pitch-deck-header"
import { PitchSuggestionModal } from "../pitch-suggestion-modal"

import { DECK_TYPE_CONFIG, MIN_PROMPT_LENGTH } from "./types"
import type { PromptBarProps } from "./types"
import { getPlaceholderText } from "./placeholder"
import { AttachmentChips } from "./attachment-chips"
import { AttachPopover } from "./attach-popover"
import { SearchModeToggle } from "./search-mode-toggle"
import { DocumentsIcon, SlidesIcon, PitchDeckIcon, ResearchIcon } from "../deck-type-icons"

export function PromptBar({
    selectedDeckType,
    selectedDocumentSubtype,
    onDeepResearchClick,
    onFastSearchClick,
    onSubmit,
    isLoading,
    files = [],
    onFilesChange = () => { },
    logoFile = null,
    onLogoChange = () => { },
    prompt = "",
    onPromptChange = () => { },
    selectedTemplate,
    onRemoveTemplate,
    pitchDeckDetails = {
        companyName: "",
        website: "",
        industry: "",
        stage: "",
        slideCount: "Auto"
    },
    onPitchDeckDataChange = () => { },
    onSwitchToFundraising,
}: PromptBarProps) {
    const isMobile = useIsMobile()
    const [isExpanded, setIsExpanded] = useState(false)
    const [showPitchSuggestion, setShowPitchSuggestion] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const [displayedPlaceholder, setDisplayedPlaceholder] = useState("")
    const [typingComplete, setTypingComplete] = useState(false)
    const [researchMode, setResearchMode] = useState<"fast-search" | "deep-research">("fast-search")

    const fullPlaceholder = getPlaceholderText(isMobile, selectedDeckType, selectedDocumentSubtype)

    useEffect(() => {
        setDisplayedPlaceholder("")
        setTypingComplete(false)
        let index = 0
        const interval = setInterval(() => {
            if (index < fullPlaceholder.length) {
                setDisplayedPlaceholder(fullPlaceholder.slice(0, index + 1))
                index++
            } else {
                clearInterval(interval)
                setTypingComplete(true)
            }
        }, 18)
        return () => clearInterval(interval)
    }, [fullPlaceholder])

    useEffect(() => {
        textareaRef.current?.focus()
    }, [])

    const { isAuthenticated } = useAppSelector((state) => state.auth)
    const { data: statusData } = useGetCartCreditStatusQuery(undefined, { skip: !isAuthenticated })
    const credits = statusData?.data?.credits

    const fastRemaining = credits?.fastDecks?.remaining ?? 0
    const proRemaining = credits?.proDecks?.remaining ?? 0

    const isPitchDeck = selectedDeckType === "fundraising-pitch-deck" || selectedDeckType === "business-pitch-deck"
    const isPromptEmpty = !prompt.trim()

    const handleSend = () => {
        if (isLoading) return

        if (isPitchDeck) {
            const missingFields: string[] = []
            if (!pitchDeckDetails.companyName.trim()) missingFields.push("Company Name")
            if (!pitchDeckDetails.website.trim()) missingFields.push("Website")
            if (!pitchDeckDetails.industry?.trim()) missingFields.push("Industry")
            if (!pitchDeckDetails.stage?.trim()) missingFields.push("Stage")

            if (missingFields.length > 0) {
                const formattedFields = missingFields.length === 1
                    ? missingFields[0]
                    : missingFields.slice(0, -1).join(", ") + " and " + missingFields[missingFields.length - 1]
                toast.info(`Please fill out ${formattedFields} in order to proceed.`)
                return
            }
        }

        if (selectedDeckType === "documents" && !selectedDocumentSubtype) {
            toast.info("Please select a document type in order to proceed.")
            return
        }

        if (isPromptEmpty) {
            toast.info("Please enter a prompt in order to proceed.")
            return
        }

        if (prompt.trim().length < MIN_PROMPT_LENGTH) {
            toast.error(`Please provide a more detailed prompt (at least ${MIN_PROMPT_LENGTH} characters) for better results.`)
            return
        }

        if (selectedDeckType === "Presentation" && onSwitchToFundraising && detectPitchIntent(prompt)) {
            setShowPitchSuggestion(true)
            return
        }

        onSubmit()
    }

    const removeFile = (indexToRemove: number) => {
        onFilesChange(files.filter((_, index) => index !== indexToRemove))
    }

    const DECK_ICONS: Record<string, any> = {
        "Presentation": SlidesIcon,
        "slides": SlidesIcon,
        "fundraising-pitch-deck": PitchDeckIcon,
        "documents": DocumentsIcon,
        "deep-research": ResearchIcon,
    }

    const baseConfig = DECK_TYPE_CONFIG[selectedDeckType] || {
        label: "Presentation",
        icon: SlidesIcon,
    }

    const chipConfig =
        selectedDeckType === "documents" && selectedDocumentSubtype
            ? {
                label: selectedDocumentSubtype
                    .split("-")
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(" "),
                icon: DocumentsIcon,
            }
            : {
                ...baseConfig,
                icon: DECK_ICONS[selectedDeckType] || baseConfig.icon || SlidesIcon
            }

    const ChipIcon = chipConfig.icon

    const getFreeCredits = () => {
        if (!credits) return 0

        let currentCredits = null
        if (isPitchDeck) {
            currentCredits = credits.fundraisingDecks
        } else if (selectedDeckType === "documents") {
            currentCredits = credits.documents
        } else if (selectedDeckType === "deep-research") {
            currentCredits = credits.marketResearch
        } else if (selectedDeckType === "Presentation") {
            currentCredits = researchMode === "fast-search" ? credits.fastDecks : credits.proDecks
        }

        if (currentCredits) {
            const purchased = currentCredits.purchased || 0
            if (purchased === 0) {
                const freeCount = (currentCredits.freeTotal || 0) - (currentCredits.freeUsed || 0)
                return freeCount > 0 ? freeCount : 0
            }
        }

        return 0
    }

    const freeCredits = getFreeCredits()



    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                opacity: { duration: 0.4, delay: 0.15 },
                y: { duration: 0.4, delay: 0.15 },
                layout: { type: "tween", ease: "easeOut", duration: 0.25 }
            }}
            className={cn(
                "w-full transition-all duration-300 relative",
                isExpanded ? "z-30 md:-mx-4 md:w-[calc(100%+2rem)]" : ""
            )}
        >
            {/* Fast/Pro Notch */}
            {selectedDeckType === "Presentation" && (
                <div className="absolute left-4 md:left-4 -top-[34px] z-0 flex items-end">
                    <div className={cn(
                        "flex items-center transition-colors duration-300"
                    )}>
                        <SearchModeToggle
                            onDeepResearchClick={() => {
                                setResearchMode("deep-research")
                                onDeepResearchClick()
                            }}
                            onFastSearchClick={() => {
                                setResearchMode("fast-search")
                                onFastSearchClick()
                            }}
                            fastRemaining={fastRemaining}
                            proRemaining={proRemaining}
                        />
                    </div>
                </div>
            )}

            <div
                className={cn(
                    "w-full rounded-2xl relative overflow-hidden z-10 transition-colors duration-300",
                    typingComplete && !prompt.trim() ? "border border-transparent" : "border border-slate-300",
                    isExpanded ? "bg-[#f9fafc]" : "bg-slate-50"
                )}
            >
            {typingComplete && !prompt.trim() && (
                <ShineBorder
                    shineColor={["#7C8CF8", "#8FA6C9", "#C7A6D8"]}
                    borderWidth={1}
                    duration={10}
                />
            )}
            <div className="p-4 pb-0">
                {isPitchDeck && (
                    <PitchDeckHeader
                        data={pitchDeckDetails}
                        onDataChange={onPitchDeckDataChange}
                    />
                )}

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="cursor-pointer absolute -top-1 -right-1 text-slate-400 hover:text-slate-600 transition-colors p-1"
                        title={isExpanded ? "Collapse" : "Expand"}
                    >
                        {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                    <textarea
                        ref={textareaRef}
                        autoFocus
                        value={prompt}
                        onChange={(e) => onPromptChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault()
                                handleSend()
                            }
                        }}
                        placeholder={displayedPlaceholder}
                        rows={isExpanded ? 10 : (isPitchDeck ? 3 : 3)}
                        className="w-full resize-none border-0 bg-[transparent] p-0 pr-8 text-[16px] text-black font-normal placeholder:text-slate-500 focus:outline-none focus:ring-0"
                    />
                </div>
            </div>

            <div className="px-3 pb-3 space-y-2">
                <AttachmentChips
                    logoFile={logoFile}
                    onLogoChange={onLogoChange}
                    selectedTemplate={selectedTemplate}
                    onRemoveTemplate={onRemoveTemplate}
                    files={files}
                    onRemoveFile={removeFile}
                />

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 min-w-0">
                        <AttachPopover
                            isPitchDeck={isPitchDeck}
                            isLoading={isLoading}
                            files={files}
                            onFilesChange={onFilesChange}
                            onLogoChange={onLogoChange}
                        />

                        {/* Selected deck type chip */}
                        <span className="flex-shrink-0 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[12px] md:text-[13px] font-medium text-blue-600">
                            <ChipIcon className="h-3.5 w-3.5 md:h-5 md:w-5" />
                            <span className="truncate max-w-[120px] md:max-w-none">{chipConfig.label}</span>
                            {freeCredits > 0 && (
                                <span className="ml-1 px-2 py-0.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-white rounded-full text-[10px] font-bold whitespace-nowrap shadow-sm">
                                    x{freeCredits} <span className="ml-0.5">Free</span>
                                </span>
                            )}
                        </span>


                    </div>

                    {/* Submit button */}
                    <div className="flex items-center gap-2">
                        
                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={isLoading}
                            aria-label={isLoading ? "Generating presentation" : "Submit prompt"}
                            className={cn(
                                "group flex py-1.5 px-1.5 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200",
                                isLoading
                                    ? "cursor-not-allowed opacity-50"
                                    : "cursor-pointer hover:bg-gray-200"
                            )}
                        >
                            {isLoading ? (
                                <Loader2
                                    className="h-5 w-5 animate-spin text-slate-400"
                                />
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    className="h-[25px] w-[25px]"
                                    fill="none"
                                >
                                    {/* wider angle with sharper ends and flatter tips */}
                                    <path
                                        d="M5 7 L17 12 L5 17"
                                        stroke={isLoading ? "#c8ced6ff" : "#252525ff"}
                                        strokeWidth="5.5"
                                        strokeLinecap="butt"
                                        strokeLinejoin="miter"
                                        strokeMiterlimit="10"
                                    />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            </div>

            <PitchSuggestionModal
                isOpen={showPitchSuggestion}
                onUseFundraising={() => {
                    setShowPitchSuggestion(false)
                    onSwitchToFundraising?.()
                }}
                onContinuePresentation={() => {
                    setShowPitchSuggestion(false)
                    onSubmit()
                }}
                onClose={() => setShowPitchSuggestion(!showPitchSuggestion)}
            />
        </motion.div>
    )
}
